import React from 'react';
import { CheckCircle, Target, Users, Award, TrendingUp, Shield } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      id: 'integrity',
      icon: Shield,
      title: 'Integrity',
      description: 'We uphold the highest standards of professional ethics and transparency in all our dealings.',
    },
    {
      id: 'excellence',
      icon: Award,
      title: 'Excellence',
      description: 'Committed to delivering superior quality services that exceed client expectations.',
    },
    {
      id: 'client-focus',
      icon: Users,
      title: 'Client Focus',
      description: 'Your success is our success. We prioritize understanding and meeting your unique needs.',
    },
    {
      id: 'innovation',
      icon: TrendingUp,
      title: 'Innovation',
      description: 'Staying ahead with the latest financial regulations and technological advancements.',
    },
  ];

  const expertise = [
    'Taxation and Litigation',
    'Corporate Law and Compliance',
    'Financial Accounting',
    'GST Implementation',
    'Audit and Assurance',
    'Business Advisory and Estate Planning',
    'Investment Planning',
    'Risk Management',
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-blue-700 to-blue-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1718220216044-006f43e3a9b1"
            alt="Office"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Us</h1>
            <p className="text-xl text-blue-100">
              Your Trusted Partner in Financial Excellence
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <img
                src="https://images.pexels.com/photos/8296998/pexels-photo-8296998.jpeg"
                alt="Professional Financial Services"
                className="rounded-2xl shadow-2xl w-full h-auto max-w-md mx-auto"
              />
            </div>
            <div className="lg:col-span-3 space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Welcome to Kanish Jariwala & Associates
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                Established as one of Mumbai's premier chartered accountant firms, we bring together 
                years of professional expertise with a commitment to personalized service excellence. 
                Our team of qualified professionals specializes in providing comprehensive financial 
                solutions to businesses of all sizes.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                From startups to established enterprises, we partner with our clients to navigate 
                the complex landscape of taxation, compliance, and financial management. Our approach 
                combines deep industry knowledge with innovative strategies to deliver results that 
                drive business growth and success.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Based in the heart of Mumbai's business district, we serve clients across various 
                industries, offering tailored solutions that address their unique challenges and 
                opportunities. Our reputation is built on trust, reliability, and a track record 
                of helping businesses achieve their financial goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="border-2 border-blue-200 shadow-xl">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                    <Target className="text-blue-700" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To deliver thoughtful, high-quality financial and tax advisory that goes beyond routine compliance. We work closely with our clients to bring clarity to complex financial matters, ensure accuracy in every detail, and provide guidance they can rely on. Our approach is built on precision, consistency, and a genuine commitment to helping businesses operate with confidence and control.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-xl">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                    <Award className="text-blue-700" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To be a trusted and respected firm known for professionalism, integrity, and the long-term relationships we build. We strive to set a higher standard in the way our services are delivered, ensuring clients feel supported, well-advised, and confident in every financial decision. Our focus remains on creating lasting value through dependable service and a deep understanding of each client's needs.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div 
                key={value.id}
                className="text-center p-8 rounded-2xl bg-blue-50 hover:bg-blue-100 transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-20 h-20 bg-blue-700 rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="text-white" size={36} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Areas of Expertise
              </h2>
              <p className="text-xl text-gray-600">
                Comprehensive knowledge across key financial domains
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {expertise.map((area, index) => (
                <div 
                  key={`expertise-${index}`}
                  className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-blue-700" size={24} />
                  </div>
                  <span className="text-lg font-medium text-gray-900">{area}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-r from-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">100+</div>
              <div className="text-blue-200">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">5+</div>
              <div className="text-blue-200">Years of Excellence</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">100%</div>
              <div className="text-blue-200">End to End Support</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-blue-200">Cases Handled</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
